import {
  AmenityReturn,
  ReservationErrorCode,
  ReservationRuleCode,
  ReservationRuleViolation,
} from '../clients/rust-api-client';
import { billingErrorMessage } from '../utils/billingHelpers';
import { formatDuration, formatHoursSpan, formatLocalDate } from './reservationFormat';

/**
 * Every refusal the rule engine can give, in words a homeowner can act
 * on. The backend's own `message` is written for staff ("per-home weekly
 * limit reached (2)"); a homeowner never sees a raw code.
 *
 * `amenity` fills in the numbers when the page has them. Every code has
 * a sentence without it too, because a 422 can arrive before the amenity
 * has loaded.
 *
 * It takes only the fields it reads, so a group member (which carries the
 * lengths and party size but not the whole configuration) fills in what
 * it can.
 */
export type RuleAmenity = Pick<AmenityReturn, 'name'> &
  Partial<
    Pick<
      AmenityReturn,
      | 'booking_block_until'
      | 'capacity'
      | 'group_name'
      | 'max_duration_minutes'
      | 'max_lead_days'
      | 'max_nights'
      | 'max_party_size'
      | 'min_duration_minutes'
      | 'min_nights'
      | 'min_notice_hours'
    >
  >;

export const ruleMessage = (
  code: ReservationRuleCode | string,
  amenity?: RuleAmenity | null
): string => {
  const name = amenity?.name ?? 'this amenity';
  switch (code) {
    case 'AMENITY_INACTIVE':
      return `${capitalise(name)} isn't taking bookings right now.`;
    case 'AMENITY_NOT_BOOKABLE':
      return `${capitalise(name)} doesn't take bookings — it's open to everyone during its hours.`;
    case 'HOME_NOT_IN_HOA':
      return `${capitalise(name)} belongs to a different association from the home you've selected.`;
    case 'INVALID_TIME_RANGE':
      return "That time doesn't work — pick a start time from the list and a length that fits.";
    case 'IN_PAST':
      return 'That time has already passed. Please pick a later one.';
    case 'SLOT_CONFLICT':
      return 'Someone booked that time just before you. Please pick another.';
    case 'QUOTE_CHANGED':
      return 'The price changed since you started. Please check the new fee and confirm again.';
    case 'DURATION_TOO_SHORT':
      return amenity && amenity.min_duration_minutes != null
        ? `Bookings of ${name} must be at least ${formatDuration(amenity.min_duration_minutes)}.`
        : 'That booking is too short.';
    case 'DURATION_TOO_LONG':
      return amenity && amenity.max_duration_minutes != null
        ? `Bookings of ${name} can be at most ${formatDuration(amenity.max_duration_minutes)}.`
        : 'That booking is too long.';
    case 'OUTSIDE_OPEN_HOURS':
      return `${capitalise(name)} isn't open for all of that time. Check its hours and try a shorter booking.`;
    case 'CLOSED':
      return `${capitalise(name)} is closed for part of that time.`;
    case 'TOO_SOON':
      return amenity?.min_notice_hours
        ? `${capitalise(name)} needs at least ${possessive(formatHoursSpan(amenity.min_notice_hours))} notice.`
        : "That's too soon to book. Please pick a later time.";
    case 'TOO_FAR_AHEAD':
      return amenity?.max_lead_days
        ? `${capitalise(name)} can only be booked up to ${amenity.max_lead_days} days ahead.`
        : "That's too far ahead to book yet.";
    case 'PARTY_SIZE_EXCEEDED':
      return amenity?.max_party_size
        ? `${capitalise(name)} holds up to ${amenity.max_party_size} people.`
        : 'Your group is larger than this amenity allows.';
    case 'HOME_DAILY_LIMIT':
      return `Your household has reached today's limit for ${name}.`;
    case 'HOME_WEEKLY_LIMIT':
      return `Your household has reached this week's limit for ${name}.`;
    case 'HOME_MONTHLY_LIMIT':
      return `Your household has reached this month's limit for ${name}.`;
    case 'HOME_ACTIVE_LIMIT':
      return `Your household already has as many upcoming bookings of ${name} as it can hold. Cancel one, or wait until it has passed.`;
    case 'ROLE_NOT_ELIGIBLE':
      return `Only owners can book ${name}.`;
    case 'NOT_IN_GOOD_STANDING':
      return `Please contact your management company about booking ${name}.`;
    case 'AGREEMENT_REQUIRED':
      return 'Please read and accept the rental agreement to book.';
    case 'AGREEMENT_VERSION_STALE':
      return 'The rental agreement was updated. Please read the new version and accept it.';
    case 'CAPACITY_EXCEEDED':
      return amenity?.capacity
        ? `There isn't room for your group then — ${name} holds ${amenity.capacity} people at a time. Try a smaller group or another time.`
        : `There isn't room for your group at that time. Try a smaller group or another time.`;
    case 'NIGHTS_TOO_FEW':
      return amenity?.min_nights
        ? `Stays at ${name} are at least ${amenity.min_nights === 1 ? '1 night' : `${amenity.min_nights} nights`}.`
        : 'That stay is too short.';
    case 'NIGHTS_TOO_MANY':
      return amenity?.max_nights
        ? `Stays at ${name} are at most ${amenity.max_nights === 1 ? '1 night' : `${amenity.max_nights} nights`}.`
        : 'That stay is too long.';
    case 'VEHICLE_REQUIRED':
      return "Please enter the vehicle's licence plate.";
    case 'OUT_OF_SEASON':
      return `${capitalise(name)} is closed for the season then.`;
    case 'HOME_WEEKLY_HOURS_LIMIT':
      return `That would take your household past this week's hours for ${name}.`;
    case 'HOME_MONTHLY_HOURS_LIMIT':
      return `That would take your household past this month's hours for ${name}.`;
    case 'GROUP_DAILY_LIMIT':
      return `Your household has reached today's limit for ${groupName(amenity)}.`;
    case 'GROUP_WEEKLY_LIMIT':
      return `Your household has reached this week's limit for ${groupName(amenity)}.`;
    case 'GROUP_MONTHLY_LIMIT':
      return `Your household has reached this month's limit for ${groupName(amenity)}.`;
    case 'GROUP_ACTIVE_LIMIT':
      return `Your household already has as many upcoming bookings of ${groupName(amenity)} as it can hold. Cancel one, or wait until it has passed.`;
    case 'HOME_SUSPENDED':
      return amenity?.booking_block_until
        ? `Your household can't book ${name} until ${formatLocalDate(amenity.booking_block_until)}. Please contact your management company with any questions.`
        : `Your household can't book ${name} right now. Please contact your management company with any questions.`;
    case 'DOCUMENTS_MISSING':
      return 'The documents this booking needs have not all been accepted yet.';
    default:
      return 'This booking breaks one of the amenity’s rules.';
  }
};

/**
 * Why this household can't book the amenity at all, before it tries —
 * from the server's `can_book` / `booking_block`, which run the same
 * checks as a booking. `null` when nothing stands in the way, and for an
 * information-only amenity, whose page says so in its own words.
 */
export const bookingBlockMessage = (amenity: AmenityReturn): string | null => {
  if (amenity.can_book !== false || !amenity.booking_block) return null;
  if (amenity.booking_block === 'AMENITY_NOT_BOOKABLE') return null;
  return ruleMessage(amenity.booking_block, amenity);
};

const capitalise = (s: string): string =>
  s.length ? s[0].toUpperCase() + s.slice(1) : s;

/** The group's name when the amenity is in one, else a fair stand-in. */
const groupName = (amenity?: RuleAmenity | null): string =>
  amenity?.group_name ?? (amenity ? `${amenity.name} and the amenities booked with it` : 'these amenities');

/** `48 hours'`, `1 hour's`. */
const possessive = (s: string): string => (s.endsWith('s') ? `${s}'` : `${s}'s`);

/**
 * The rule violations in a failed booking, or `null` when the failure was
 * something else (a 404, a 409, the network). A 422 from a booking is
 * always `{ error, violations: [...] }`.
 */
export const violationsFromError = (
  err: unknown
): ReservationRuleViolation[] | null => {
  const response = (err as { response?: { status?: number; data?: unknown } })
    ?.response;
  if (response?.status !== 422) return null;
  const data = response.data as { violations?: unknown } | undefined;
  return Array.isArray(data?.violations)
    ? (data!.violations as ReservationRuleViolation[])
    : null;
};

/**
 * The machine-readable `code` of a reservation error that was not a rule
 * (`ReservationErrorReturn`, every 400/404/409/500 from the reservation
 * API), or `null` when the failure carries none — the network, or an
 * older server. Branch on this, never on the prose in `error`.
 */
export const reservationErrorCode = (err: unknown): ReservationErrorCode | null => {
  const data = (err as { response?: { data?: unknown } })?.response?.data as
    | { code?: unknown }
    | undefined;
  return typeof data?.code === 'string' ? (data.code as ReservationErrorCode) : null;
};

/**
 * The codes a homeowner can run into on their own booking, in their words.
 * The rest (a stale amenity edit, a season overlap, …) are management's,
 * and fall through to the server's message.
 */
const householdCodeMessage = (code: ReservationErrorCode | null): string | null => {
  switch (code) {
    case 'WRONG_STATUS':
      return 'This booking has changed since you opened it. Refresh to see where it stands.';
    case 'ALREADY_STARTED':
      return 'This booking has already started.';
    case 'ALREADY_OVER':
      return 'This booking is already over.';
    case 'DEADLINE_PASSED':
      return "It's past the cancellation deadline for this booking.";
    case 'AMENITY_ARCHIVED':
      return 'This amenity is no longer taking bookings.';
    case 'WAITLIST_SLOT_FREE':
      return "There's a free time in that window right now — book it instead of waiting.";
    case 'WAITLIST_LIMIT':
      return 'Your household is already waiting for as many times as it can. Leave one waitlist to join another.';
    case 'WAITLIST_NO_OFFER':
      return 'That offer is no longer open — it may have been passed to the next household.';
    case 'WAITLIST_CLOSED':
      return 'That waitlist entry has closed.';
    case 'GROUP_CHANGED':
      return 'The amenities in this group changed while you were booking. Please check the times again.';
    default:
      return null;
  }
};

/**
 * What to tell a homeowner when a reservation call fails for a reason
 * that is not a rule. 404 is "not yours or not there" by design, and is
 * said as not found, never as forbidden.
 */
export const reservationErrorMessage = (
  err: unknown,
  fallback: string
): string => {
  const status = (err as { response?: { status?: number } })?.response?.status;
  if (status === 404) {
    return "We couldn't find that. It may have been removed.";
  }
  if (status === 403) {
    return 'This is a read-only view, so nothing can be booked or cancelled here.';
  }
  return householdCodeMessage(reservationErrorCode(err)) ?? billingErrorMessage(err, fallback);
};

/** The `FEE_PAID` refusal, worded once for the error and the preview. */
export const FEE_PAID_MESSAGE =
  "You've already paid for this booking, and the new time costs a different amount. Please ask your management company to move it.";

/**
 * Why a move was refused when it was not a rule (#640 phase 2). Two
 * refusals a homeowner hits: the fee is paid and the move changes it
 * (`FEE_PAID` — management can do it and refund the difference), and the
 * deadline has passed.
 */
export const rescheduleErrorMessage = (err: unknown): string => {
  switch (reservationErrorCode(err)) {
    case 'FEE_PAID':
      return FEE_PAID_MESSAGE;
    case 'DEADLINE_PASSED':
      return "It's past the cancellation deadline, so this booking can't be moved now.";
    case 'ALREADY_STARTED':
    case 'ALREADY_OVER':
    case 'WRONG_STATUS':
      return "This booking can't be moved any more.";
    default:
      return reservationErrorMessage(err, "We couldn't move this booking.");
  }
};
